import { KeyboardEvent, useMemo, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { SelectButton } from 'primereact/selectbutton'
import { OperatingRoomHeatMeasure, OperatingRoomHeatmapDTO } from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { weekdayNames } from '../../ServicesStatistics/chartText'
import { mixHex, useChartTokens } from '../../ServicesStatistics/chartTokens'
import { SectionState } from '../../UrgencySummary/useSectionData'
import { formatCount, formatHoursPlain } from '../operatingRoomFormat'

const HEAT_MEASURES: OperatingRoomHeatMeasure[] = ['HOURS', 'STARTED']
const MEASURE_LABEL_ID: Record<OperatingRoomHeatMeasure, string> = {
  HOURS: 'OperatingRoomIndicatorsHeatHours',
  STARTED: 'OperatingRoomIndicatorsHeatStarted',
}

/** Escalones de la escala: del más claro (poca actividad) al más oscuro. */
const STEPS = 5
/** Desde este escalón el texto de la celda va en claro (contraste sobre --teal-700 y --teal-800). */
const DARK_FROM_STEP = 4
const HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const DAYS = Array.from({ length: 7 }, (_, day) => day)

/** El valor de una celda escrito: las horas con un decimal y las cirugías, enteras; vacío si es cero. */
function cellText(intl: IntlShape, measure: OperatingRoomHeatMeasure, value: number): string {
  if (value <= 0) return ''
  return measure === 'HOURS' ? formatHoursPlain(intl, value) : formatCount(intl, value)
}

interface OperatingRoomHeatmapProps {
  heatmap: SectionState<OperatingRoomHeatmapDTO>
  measure: OperatingRoomHeatMeasure
  onMeasureChange: (measure: OperatingRoomHeatMeasure) => void
  /** Una celda con valor: lista sus cirugías (`dayOfWeek` 1 = lunes … 7 = domingo). */
  onCell: (dayOfWeek: number, hour: number, measure: OperatingRoomHeatMeasure) => void
}

/**
 * Mapa de calor del día de la semana por hora real (lunes a domingo, 24 horas), con la medida a elegir: horas de sala
 * utilizadas o cirugías iniciadas. Es una tabla de verdad: cada celda lleva su número escrito (el color nunca es el único
 * portador del dato), con los totales por día y por hora, y cada celda con valor abre la lista con las cirugías que cuenta. Con el
 * teclado: una sola celda en el orden de tabulación y las flechas, Inicio y Fin mueven entre celdas. La escala sale de los tokens
 * teal, la misma de Estadísticas de atención.
 * TODO(design-system): gráficos y mapa de calor. Novarium no define la escala de colores del mapa de calor.
 */
export default function OperatingRoomHeatmap({ heatmap, measure, onMeasureChange, onCell }: OperatingRoomHeatmapProps) {
  const intl = useIntl()
  const ref = useRef<HTMLElement>(null)
  const tokens = useChartTokens(ref)
  const days = useMemo(() => weekdayNames(intl), [intl])
  const [active, setActive] = useState<[number, number]>([0, 8])
  const data = heatmap.data ?? heatmap.stale
  const refreshing = heatmap.status === 'loading'
  const max = data?.max ?? 0

  const measureOptions = HEAT_MEASURES.map((value) => ({ value, label: intl.formatMessage({ id: MEASURE_LABEL_ID[value] }) }))

  /** El escalón de la escala de una celda (1 a STEPS); 0 si no tiene valor. */
  const stepOf = (value: number): number => (value <= 0 || max <= 0 ? 0 : Math.min(STEPS, Math.max(1, Math.ceil((value / max) * STEPS))))
  const colorOf = (step: number): string | undefined =>
    (tokens && step > 0 ? mixHex(tokens.teal300, tokens.teal800, (step - 1) / (STEPS - 1)) : undefined)

  const onKeyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    const [row, col] = active
    let next: [number, number] | null = null
    if (event.key === 'ArrowRight') next = [row, Math.min(23, col + 1)]
    else if (event.key === 'ArrowLeft') next = [row, Math.max(0, col - 1)]
    else if (event.key === 'ArrowDown') next = [Math.min(6, row + 1), col]
    else if (event.key === 'ArrowUp') next = [Math.max(0, row - 1), col]
    else if (event.key === 'Home') next = [row, 0]
    else if (event.key === 'End') next = [row, 23]
    if (!next) return
    event.preventDefault()
    setActive(next)
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-cell="${next[0]}-${next[1]}"]`)?.focus()
  }

  return (
    <section ref={ref} className="nv-or-heat" aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsTabHeatmap' })} aria-busy={refreshing}>
      <div className="nv-or-heat__tools">
        <SelectButton
          value={measure}
          options={measureOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(event) => { if (event.value) onMeasureChange(event.value as OperatingRoomHeatMeasure) }}
          aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsMeasureBy' })}
        />
      </div>

      {refreshing && data === null && (
        <span className="nv-loading" role="status">
          <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
          <span>{intl.formatMessage({ id: 'OperatingRoomIndicatorsLoading' })}</span>
        </span>
      )}
      {heatmap.status === 'failed' && (
        <div className="nv-or-failed" role="alert">
          <p className="nv-note">
            <i className="pi pi-info-circle" aria-hidden="true" />
            {intl.formatMessage({ id: 'OperatingRoomIndicatorsHeatmapError' })}
          </p>
          <Button type="button" link label={intl.formatMessage({ id: 'OperatingRoomIndicatorsRetry' })} onClick={heatmap.retry} />
        </div>
      )}

      {data !== null && heatmap.status !== 'failed' && (data.total <= 0
        ? <p className="nv-muted">{intl.formatMessage({ id: 'OperatingRoomIndicatorsEmpty' })}</p>
        : (
          <div className={`nv-or-heat__body${refreshing ? ' nv-or-blurred' : ''}`}>
            <div className="nv-or-heat__scroll">
              <table className="nv-or-heat__table" onKeyDown={onKeyDown}>
                <caption>{intl.formatMessage({ id: measure === 'HOURS' ? 'OperatingRoomIndicatorsHeatCaptionHours' : 'OperatingRoomIndicatorsHeatCaptionStarted' })}</caption>
                <thead>
                  <tr>
                    <th scope="col"><span className="nv-sr-only">{intl.formatMessage({ id: 'OperatingRoomIndicatorsHeatDay' })}</span></th>
                    {HOURS.map((hour) => <th key={hour} scope="col">{String(hour).padStart(2, '0')}</th>)}
                    <th scope="col">{intl.formatMessage({ id: 'OperatingRoomIndicatorsTotal' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map((day) => (
                    <tr key={day}>
                      <th scope="row">{days[day]}</th>
                      {HOURS.map((hour) => {
                        const value = data.cells[day]?.[hour] ?? 0
                        const step = stepOf(value)
                        const text = cellText(intl, measure, value)
                        const label = intl.formatMessage({ id: 'OperatingRoomIndicatorsHeatCellLabel' }, {
                          day: days[day],
                          hour: `${String(hour).padStart(2, '0')}:00`,
                          value: measure === 'HOURS'
                            ? intl.formatMessage({ id: 'OperatingRoomIndicatorsHoursValue' }, { value: formatHoursPlain(intl, value) })
                            : intl.formatMessage({ id: 'OperatingRoomIndicatorsHeatStartedValue' }, { count: value, countText: formatCount(intl, value) }),
                        })
                        return (
                          <td
                            key={hour}
                            className={`nv-or-heat__cell${step >= DARK_FROM_STEP ? ' nv-or-heat__cell--dark' : ''}${step === 0 ? ' nv-or-heat__cell--empty' : ''}`}
                            style={step > 0 ? { background: colorOf(step) } : undefined}
                          >
                            <button
                              type="button"
                              data-cell={`${day}-${hour}`}
                              tabIndex={active[0] === day && active[1] === hour ? 0 : -1}
                              aria-label={label}
                              aria-disabled={value <= 0}
                              onFocus={() => setActive([day, hour])}
                              onClick={() => { if (value > 0) onCell(day + 1, hour, measure) }}
                            >
                              {text}
                            </button>
                          </td>
                        )
                      })}
                      <td className="nv-or-heat__total">
                        {measure === 'HOURS' ? formatHoursPlain(intl, data.rowTotals[day] ?? 0) : formatCount(intl, data.rowTotals[day] ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">{intl.formatMessage({ id: 'OperatingRoomIndicatorsTotal' })}</th>
                    {HOURS.map((hour) => (
                      <th key={hour} scope="col" className="nv-or-heat__total">
                        {cellText(intl, measure, data.colTotals[hour] ?? 0)}
                      </th>
                    ))}
                    <th scope="col" className="nv-or-heat__total">
                      {measure === 'HOURS' ? formatHoursPlain(intl, data.total) : formatCount(intl, data.total)}
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="nv-or-heat__legend" aria-hidden="true">
              <span>0</span>
              {Array.from({ length: STEPS }, (_, index) => (
                <span key={index} className="nv-or-heat__swatch" style={{ background: colorOf(index + 1) }} />
              ))}
              <span>{measure === 'HOURS' ? formatHoursPlain(intl, max) : formatCount(intl, max)}</span>
            </div>
          </div>
        ))}
    </section>
  )
}
